<template>
  <div
    class="FormChecklist"
  >
    <FormChecklistItem
      v-for="option in options"
      :key="option.label"
      v-model="modelValue"
      v-bind="{...option, type}"
    />
  </div>
</template>

<script lang="ts" generic="T" setup>
import { FormChecklistOption } from "@/components/FormChecklist.types";
import FormChecklistItem from "@/components/FormChecklistItem.vue";

const modelValue = defineModel<T | Array<T>>({
  required: true,
});

const {
  type = "checkbox",
} = defineProps<{
  options: Array<FormChecklistOption<T>>,
  type?: "checkbox" | "radio",
}>();
</script>

<style lang="scss" scoped>
.FormChecklist {
  --inputHeight: 48px;
  --highlightWidth: 3px;
  --outlineWidth: 2px;
  --inputInnerHeight: calc(var(--inputHeight) - 2 * var(--outlineWidth));
  --iconWidth: var(--inputInnerHeight);
  --offsetWidth: calc(var(--highlightWidth) + var(--outlineWidth));
  --borderRadius: calc(6px + var(--offsetWidth));
  position: relative;
  isolation: isolate;

  display: grid;
  gap: 1px;
  min-height: var(--inputInnerHeight);
  padding: var(--outlineWidth);

  border-radius: var(--borderRadius);
  background-color: var(--themeColorInputBackground);
  color: var(--themeColorInputForeground);
  box-shadow: 0 0 0 var(--highlightWidth) transparent inset;

  transition: 350ms;
  transition-property: color, background-color, box-shadow;

  [data-theme="light"] & {
    --highlightWidth: 0px;
    --outlineColorDisabled: var(--themeColorNeutral400);
    box-shadow: 0 0 0 var(--highlightWidth) transparent;
  }

  &::before {
    position: absolute;
    top: 0;
    left: 0;
    z-index: -1;

    width: var(--iconWidth);
    height: 100%;

    border-radius: var(--borderRadius) 0 0 var(--borderRadius);
    background-color: var(--themeColorNeutral200);

    content: "";
  }

  &::after {
    position: absolute;
    inset: 0;

    border-radius: inherit;
    outline: var(--outlineWidth) solid var(--themeColorNeutral900);
    outline-offset: calc(-1 * var(--offsetWidth));

    transition: outline-color var(--transitionDuration);

    pointer-events: none;
    content: "";
  }

  &:has(:disabled) {
    background-color: var(--themeLightTransparent600);
    color: var(--themeDarkTransparent500);

    &::after {
      outline-color: var(--themeColorNeutral400);
    }
  }

  &:focus-within,
  &:has(:active) {
    box-shadow: 0 0 0 var(--highlightWidth) var(--themeColorSecondary) inset;

    [data-theme="light"] & {
      box-shadow: 0 0 0 3px var(--themeColorSecondary);
    }
  }
}
</style>
